import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar, Paper, Grid, Checkbox,
  IconButton, Button, TablePagination, TextField, FormControl, InputLabel, Select, MenuItem, ListItemText, Slider, Chip,
  Switch
} from '@mui/material';
import {
  Delete as DeleteIcon,
  TrendingUp,
  TrendingFlat,
  TrendingDown,
  Healing as HealingIcon,
  Assignment as AssignmentIcon,
  Lightbulb as LightbulbIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  Straighten as StraightenIcon,
  FolderOpen as FolderOpenIcon,
  Visibility as VisibilityIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { SeguimientoContext } from '../SeguimientoContext';
import BigBoardFilters from '../components/BigBoardFilters';
import FlagDisplay from '../components/FlagDisplay';
import { DB_TO_TRANSLATION_KEY } from '../utils/positions';

const Seguimiento = () => {
  const { t } = useTranslation();
  const [players, setPlayers] = useState([]);
  const { seguimientoPlayers, setSeguimientoPlayers } = useContext(SeguimientoContext);

  // Pagination
  const [page, setPage] = useState(0);
  const rowsPerPage = 50;

  // -- FUSION des filtres nom/équipe en un seul champ --
  const [searchFilter, setSearchFilter] = useState('');

  // -- MULTISELECT "Filtrar por grupos" --
  const [groups, setGroups] = useState([]);       // Liste des groupes récupérés depuis l'API
  const [groupFilter, setGroupFilter] = useState([]); // IDs de groupes sélectionnés

  // -- Positions, ligues, taille/âge --
  const [positionFilter, setPositionFilter] = useState([]);
  const [heightFilter, setHeightFilter] = useState([100, 230]);
  const [ageFilter, setAgeFilter] = useState([8, 42]);
  const [uniqueLeagues, setUniqueLeagues] = useState([]);
  const [leagueFilter, setLeagueFilter] = useState([]);

  // New filter states from BigBoard
  const [nameFilter, setNameFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [playerTypeFilter, setPlayerTypeFilter] = useState(0);
  const [informeFilter, setInformeFilter] = useState(null);
  const [scoutFilter, setScoutFilter] = useState(null);
  const [verdictFilter, setVerdictFilter] = useState([]);
  const [groupColors, setGroupColors] = useState({});

  // -- Player Type (Pro / Cantera) --
  const [playerType, setPlayerType] = useState('Pro'); // Valeur par défaut

  // -- Récupération du rôle utilisateur --
  const userData = JSON.parse(localStorage.getItem('user'));
  const userRole = userData?.role?.toLowerCase() || '';

  // -- Gestion du nom du Seguimiento (Select + "Otro") --
  const [selectedSeguimientoNameOption, setSelectedSeguimientoNameOption] = useState('');
  const [seguimientoName, setSeguimientoName] = useState('');

  // -- Tags --
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // -- Contrôle d'URL et chargement initial --
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // -- ENV --
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;
  const mediaUrl = getMediaBaseUrl();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));


  // Helper components from BigBoard
  const FormaIndicator = ({ forma }) => {
    if (!forma) return null;

    switch (forma?.toLowerCase()) {
      case 'high':
        return <TrendingUp sx={{ color: '#4caf50', fontSize: 20 }} />;
      case 'stable':
        return <TrendingFlat sx={{ color: '#ff9800', fontSize: 20 }} />;
      case 'low':
        return <TrendingDown sx={{ color: '#f44336', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const VerdictIndicator = ({ player }) => {
    if (!player.scout_verdicts || player.scout_verdicts.length === 0) return null;

    // Group verdicts by type
    const verdictGroups = player.scout_verdicts.reduce((acc, verdict) => {
      if (!acc[verdict.verdict]) {
        acc[verdict.verdict] = [];
      }
      acc[verdict.verdict].push(verdict);
      return acc;
    }, {});

    const verdictConfig = {
      'VOLVER A VER': { emoji: '🔄', color: '#2196f3' },
      'DESCARTADO': { emoji: '❌', color: '#f44336' },
      'MONITORIZAR': { emoji: '👁️', color: '#ff9800' },
      'PRIORITARIO': { emoji: '⭐', color: '#4caf50' },
      'DUDAS': { emoji: '❓', color: '#9c27b0' }
    };

    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {Object.entries(verdictGroups).map(([verdictType, verdicts]) => {
          const config = verdictConfig[verdictType];
          if (!config) return null;

          const count = verdicts.length;

          return (
            <Box key={verdictType} sx={{ position: 'relative', display: 'inline-block' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    transition: 'transform 0.2s'
                  }
                }}
              >
                {config.emoji}
              </Box>
              {count > 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    backgroundColor: config.color,
                    color: '#fff',
                    borderRadius: '50%',
                    width: 12,
                    height: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 8,
                    fontWeight: 'bold',
                    border: '1px solid #fff',
                  }}
                >
                  {count}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const CountBadge = ({ count }) => {
    const backgroundColor = count <= 1 ? '#f44336' : '#4caf50';

    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          backgroundColor: backgroundColor,
          color: '#ffffff',
          width: '28px',
          height: '28px',
          fontSize: '12px',
          fontWeight: 'bold',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          border: '2px solid #fff',
        }}
      >
        {count}
      </Box>
    );
  };

  const badgeStyle = (bgColor) => ({
    position: 'relative',
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: bgColor,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '14px',
    marginLeft: '-4px',
    border: '1px solid #fff',
  });

  const calculateDecimalAge = (anneeNaissance) => {
    if (!anneeNaissance) return '';
    const currentYear = new Date().getFullYear();
    const age = currentYear - anneeNaissance;
    return age;
  };

  const getContrastColor = (hexcolor) => {
    if (!hexcolor) return '#000000';
    const r = parseInt(hexcolor.slice(1, 3), 16);
    const g = parseInt(hexcolor.slice(3, 5), 16);
    const b = parseInt(hexcolor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  // 1) GESTION DU RÔLE ET DU PLAYER TYPE
  useEffect(() => {
    if (userRole === 'super admin') {
      // Laisse le switch Pro/Cantera
      return;
    } else if (userRole === 'admin pro') {
      setPlayerType('Pro');
    } else {
      // 'admin cantera', 'entrenador', 'pdp', etc.
      setPlayerType('Cantera');
    }
  }, [userRole]);

  // 2) CHARGEMENT DES FILTRES DEPUIS L'URL (premier rendu)
  useEffect(() => {
    if (isInitialLoad) {
      const params = new URLSearchParams(location.search);
      setSearchFilter(params.get('searchFilter') || '');
      setPositionFilter(params.get('positionFilter') ? params.get('positionFilter').split(',') : []);
      setLeagueFilter(params.get('leagueFilter') ? params.get('leagueFilter').split(',') : []);
      setGroupFilter(params.get('groupFilter') ? params.get('groupFilter').split(',').map(Number) : []);
      setHeightFilter(
        params.get('heightFilter')
          ? params.get('heightFilter').split(',').map(Number)
          : [130, 230]
      );
      setAgeFilter(
        params.get('ageFilter')
          ? params.get('ageFilter').split(',').map(Number)
          : [12, 45]
      );
      setPage(params.get('page') ? parseInt(params.get('page'), 10) : 0);

      setIsInitialLoad(false);
    }
  }, [location.search, isInitialLoad]);

  // 3) MISE À JOUR DE L'URL QUAND LES FILTRES CHANGENT
  useEffect(() => {
    if (!isInitialLoad) {
      const params = new URLSearchParams();
      if (searchFilter) params.set('searchFilter', searchFilter);
      if (positionFilter.length) params.set('positionFilter', positionFilter.join(','));
      if (leagueFilter.length) params.set('leagueFilter', leagueFilter.join(','));
      if (groupFilter.length) params.set('groupFilter', groupFilter.join(','));
      if (heightFilter.length) params.set('heightFilter', heightFilter.join(','));
      if (ageFilter.length) params.set('ageFilter', ageFilter.join(','));
      if (page) params.set('page', page);

      const newUrl = `${location.pathname}?${params.toString()}`;
      if (location.search !== `?${params.toString()}`) {
        navigate(newUrl, { replace: true });
      }
    }
  }, [
    searchFilter,
    positionFilter,
    leagueFilter,
    groupFilter,
    heightFilter,
    ageFilter,
    page,
    isInitialLoad,
    location.pathname,
    location.search,
    navigate
  ]);

  // 4) RÉCUP DES JOUEURS (API)
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await api.get('/api/players');
        const allPlayers = response.data.players;

        // Ligues uniques
        const leagues = Array.from(
          new Set(
            allPlayers
              .map((p) => p.league_1)
              .filter(
                (league) =>
                  league !== null &&
                  league !== undefined &&
                  typeof league === 'string' &&
                  league.trim() !== '' &&
                  league.trim() !== 'null'
              )
          )
        ).sort();
        setUniqueLeagues(leagues);

        // Exclure joueurs déjà dans "seguimiento"
        const seguimientoPlayerIds = seguimientoPlayers.map((p) => p.id);
        const availablePlayers = allPlayers.filter(
          (p) => !seguimientoPlayerIds.includes(p.id)
        );

        setPlayers(availablePlayers);
      } catch (error) {
        console.error('Erreur lors de la récupération des joueurs :', error);
      }
    };
    fetchPlayers();
  }, [backendUrl, section, seguimientoPlayers]);

  // Récupération de la liste des groupes
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await api.get('/api/groups');
        // On suppose que response.data est un tableau d'objets { id, libelle, code_couleur }
        setGroups(response.data);

        // Create color mapping for groups
        const colors = {};
        response.data.forEach(group => {
          colors[group.libelle] = group.code_couleur;
        });
        setGroupColors(colors);
      } catch (error) {
        console.error('Erreur lors de la récupération des groupes:', error);
      }
    };
    fetchGroups();
  }, [backendUrl, section]);

  // 5) RÉCUP DES TAGS
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await api.get('/api/tags');
        setTags(response.data);
      } catch (error) {
        console.error('Erreur lors de la récupération des tags :', error);
      }
    };
    fetchTags();
  }, [backendUrl, section]);

  // 6) Fonction pour calculer l'âge
  const calculateAge = (anneeNaissance) => {
    const currentYear = new Date().getFullYear();
    return currentYear - anneeNaissance;
  };

  // 7) FILTRAGE
  const filteredPlayers = players.filter((player) => {
    // Player type filter
    if (playerTypeFilter === 1) {
      if (!player.type || player.type.toLowerCase() !== 'cantera') return false;
    } else if (playerTypeFilter === 2) {
      if (!player.type || player.type.toLowerCase() !== 'pro') return false;
    } else {
      // Default behavior - use playerType
      if (!player.type || player.type.toLowerCase() !== playerType.toLowerCase()) {
        return false;
      }
    }

    // Name/team search (use nameFilter or fallback to searchFilter)
    const searchTerm = (nameFilter || searchFilter).toLowerCase();
    const matchesNameTeam =
      player.nom.toLowerCase().includes(searchTerm) ||
      (player.team && player.team.toLowerCase().includes(searchTerm));

    if (!matchesNameTeam) return false;

    // Year filter
    if (yearFilter && !player.annee_naissance.toString().includes(yearFilter))
      return false;

    // Informe filter
    if (informeFilter) {
      if (informeFilter === 'zero') {
        // 0 informes: players with exactly 0 informe
        const count = player.nb_informe || 0;
        if (count !== 0) return false;
      } else if (informeFilter === 'red') {
        // 0-1 informes: players with 0 or 1 informe
        const count = player.nb_informe || 0;
        if (count > 1) return false;
      } else if (informeFilter === 'green') {
        // 2+ informes: players with 2 or more informes
        const count = player.nb_informe || 0;
        if (count <= 1) return false;
      }
    }

    // Scout filter
    if (scoutFilter) {
      if (scoutFilter === 'red') {
        // 0-1 scouts: players with 0 or 1 scout
        const count = player.nb_scout || 0;
        if (count > 1) return false;
      } else if (scoutFilter === 'green') {
        // 2+ scouts: players with 2 or more scouts
        const count = player.nb_scout || 0;
        if (count <= 1) return false;
      }
    }

    // Verdict filter
    if (verdictFilter.length > 0) {
      // Check if player has any of the selected verdicts
      if (!player.scout_verdicts || player.scout_verdicts.length === 0) {
        return false;
      }

      // Check if any of the player's verdicts match the selected filters
      const playerVerdicts = player.scout_verdicts.map(v => v.verdict);
      const hasMatchingVerdict = verdictFilter.some(v => playerVerdicts.includes(v));

      if (!hasMatchingVerdict) return false;
    }

    // Position filter - compare les clés de position (pointGuard, shootingGuard, etc.)
    const positionMatches =
      positionFilter.length === 0 ||
      positionFilter.some((posKey) => {
        // Convertir la position du joueur en clé de traduction
        const playerPositionKey = DB_TO_TRANSLATION_KEY[player.poste] || player.poste;
        return playerPositionKey === posKey;
      });
    if (!positionMatches) return false;

    // Group filter
    if (groupFilter.length > 0) {
      if (groupFilter.includes('Sin Grupos')) {
        if (!player.groupes || player.groupes.length === 0) {
          // Player has no groups - matches "Sin Grupos"
        } else if (!groupFilter.some(g => g !== 'Sin Grupos')) {
          return false;
        }
      } else {
        const playerGroupNames = (player.groupes || []).map((g) => g.libelle);
        const hasOverlap = groupFilter.some((name) => playerGroupNames.includes(name));
        if (!hasOverlap) return false;
      }
    }

    // Height filter
    if (player.taille < heightFilter[0] || player.taille > heightFilter[1]) {
      return false;
    }

    // Age filter
    const playerAge = calculateAge(player.annee_naissance);
    if (playerAge < ageFilter[0] || playerAge > ageFilter[1]) {
      return false;
    }

    return true;
  });

  // 8) Pagination : joueurs de la page courante
  const getCurrentPagePlayers = () => {
    return filteredPlayers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  // 9) AJOUT / RETRAIT JOUEUR
  const movePlayerToSeguimiento = (player) => {
    if (!seguimientoPlayers.some((p) => p.id === player.id)) {
      const updated = [...seguimientoPlayers, player].sort((a, b) =>
        a.nom.localeCompare(b.nom)
      );
      setSeguimientoPlayers(updated);
      setPlayers(players.filter((p) => p.id !== player.id));
    }
  };

  const removePlayerFromSeguimiento = (player) => {
    setSeguimientoPlayers(seguimientoPlayers.filter((p) => p.id !== player.id));
    // On le remet dans la liste "players", triés par nom
    const updated = [...players, player].sort((a, b) => a.nom.localeCompare(b.nom));
    setPlayers(updated);
  };

  // 10) Droit de voir la fiche ou non
  const canViewPlayer = !(userRole === 'entrenador' || userRole === 'pdp');

  const handleViewPlayer = (player) => {
    if (canViewPlayer) {
      navigate(`/players/${player.id}`);
    }
  };

  // 11) ENREGISTREMENT
  const handleSaveSeguimiento = async () => {
    if (!seguimientoName) {
      alert(t('seguimiento.alertEnterName'));
      return;
    }
    if (seguimientoPlayers.length === 0) {
      alert(t('seguimiento.alertAddPlayers'));
      return;
    }
    if (selectedTags.length === 0) {
      alert(t('seguimiento.alertSelectTags'));
      return;
    }

    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const userId = userData?.id;
      if (!userId) {
        alert(t('seguimiento.alertUserIdError'));
        return;
      }

      await api.post('/api/seguimiento', {
        nom: seguimientoName,
        tags: selectedTags,
        players: seguimientoPlayers.map((p) => p.id),
        user_id: userId,
        type: playerType, // Pro ou Cantera
      });

      alert(t('seguimiento.alertSaveSuccess'));

      // Reset
      setSelectedSeguimientoNameOption('');
      setSeguimientoName('');
      setSelectedTags([]);
      setSeguimientoPlayers([]);
    } catch (error) {
      console.error('Error al guardar el Seguimiento.', error);
      alert(t('seguimiento.alertSaveError'));
    }
  };

  // 12) GESTION DU SELECT POUR "Nombre del Seguimiento"
  const handleSeguimientoNameChange = (event) => {
    const value = event.target.value;
    setSelectedSeguimientoNameOption(value);
    if (value !== 'Otro') {
      setSeguimientoName(value);
    } else {
      setSeguimientoName('');
    }
  };

  // -- Composants de tableau / DND --

  // Joueur disponible (liste de gauche)
  const PlayerRow = ({ player }) => {
    const [{ isDragging }, dragRef] = useDrag({
      type: 'player',
      item: player,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const isInSeguimiento = seguimientoPlayers.some((p) => p.id === player.id);

    const handleCheckboxChange = (e) => {
      if (e.target.checked) {
        movePlayerToSeguimiento(player);
      } else {
        removePlayerFromSeguimiento(player);
      }
    };

    return (
      <TableRow
        ref={dragRef}
        key={player.id}
        hover
        sx={{
          opacity: isDragging ? 0.5 : 1,
          cursor: 'move'
        }}
      >
        <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
          <Checkbox checked={isInSeguimiento} onChange={handleCheckboxChange} />
        </TableCell>
        <TableCell sx={{ textAlign: 'left', padding: '4px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlagDisplay nationality={player.nationalite} />
            <Avatar
              src={`${mediaUrl}/photos/${player.id}.png`}
              alt={player.nom}
              sx={{ width: 36, height: 36 }}
            />
            <VerdictIndicator player={player} />
          </Box>
        </TableCell>
        <TableCell sx={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', padding: '4px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: '14px' }}>
              {canViewPlayer ? (
                <Box
                  component="a"
                  href={`/players/${player.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleViewPlayer(player);
                  }}
                  sx={{
                    color: 'black',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    },
                  }}
                >
                  {player.nom}
                </Box>
              ) : (
                <span>{player.nom}</span>
              )}
            </Typography>
            {player.is_in_injury === 1 && (
              <HealingIcon sx={{ color: 'red', fontSize: 16 }} />
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ textAlign: 'left', padding: '4px' }}>
          <FormaIndicator forma={player.forme_indicator} />
        </TableCell>
        <TableCell sx={{ textAlign: 'left', fontSize: '13px', padding: '4px' }}>
          {player.poste}
        </TableCell>
        <TableCell sx={{ textAlign: 'left', fontSize: '13px', padding: '4px' }}>
          {player.annee_naissance}
          {player.annee_naissance && ` (${calculateDecimalAge(player.annee_naissance)})`}
        </TableCell>
        <TableCell sx={{ textAlign: 'left', fontSize: '13px', padding: '4px' }}>
          {player.taille}cm
        </TableCell>
        <TableCell sx={{ textAlign: 'center', fontSize: '13px', padding: '4px' }}>
          <Box>
            <div>{player.nationalite && player.nationalite !== 'null' ? player.nationalite : ''}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {player.passport && player.passport !== 'null' ? player.passport : ''}
            </div>
          </Box>
        </TableCell>
        <TableCell sx={{ textAlign: 'center', fontSize: '13px', padding: '4px' }}>
          <Box>
            <div>{player.team}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{player.league_1}</div>
          </Box>
        </TableCell>
        <TableCell sx={{ textAlign: 'left', padding: '4px' }}>
          {player.groupes && player.groupes.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {player.groupes.slice(0, 2).map((group, idx) => (
                <Chip
                  key={idx}
                  label={group.libelle}
                  size="small"
                  sx={{
                    backgroundColor: group.code_couleur || '#grey',
                    color: getContrastColor(group.code_couleur),
                    fontSize: '11px',
                    height: '20px'
                  }}
                />
              ))}
              {player.groupes.length > 2 && (
                <Chip
                  label={`+${player.groupes.length - 2}`}
                  size="small"
                  sx={{ fontSize: '11px', height: '20px' }}
                />
              )}
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            <CountBadge count={player.nb_informe || 0} />
            <CountBadge count={player.nb_scout || 0} />
          </Box>
        </TableCell>
        <TableCell sx={{ textAlign: 'left', padding: '4px', paddingLeft: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
            {player.is_in_big_board === 1 && (
              <Box sx={badgeStyle('#1976d2')}>
                <StarIcon fontSize="inherit" />
              </Box>
            )}
            {player.is_in_follow_list === 1 && (
              <Box sx={badgeStyle('#dc004e')}>
                <VisibilityIcon fontSize="inherit" />
              </Box>
            )}
            {player.is_in_informe === 1 && (
              <Box sx={badgeStyle('#4caf50')}>
                <AssignmentIcon fontSize="inherit" />
              </Box>
            )}
            {player.is_in_intel === 1 && (
              <Box sx={badgeStyle('#ff9800')}>
                <LightbulbIcon fontSize="inherit" />
              </Box>
            )}
            {player.is_in_fiche_technique === 1 && (
              <Box sx={badgeStyle('#2196f3')}>
                <DescriptionIcon fontSize="inherit" />
              </Box>
            )}
            {player.is_in_video === 1 && (
              <Box sx={badgeStyle('#9c27b0')}>
                <VideoLibraryIcon fontSize="inherit" />
              </Box>
            )}
            {player.has_medidas === 1 && (
              <Box sx={badgeStyle('#00bcd4')}>
                <StraightenIcon fontSize="inherit" />
              </Box>
            )}
            {player.has_documents === 1 && (
              <Box sx={badgeStyle('#795548')}>
                <FolderOpenIcon fontSize="inherit" />
              </Box>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  // Joueur déjà en suivi (colonne de droite)
  const SeguimientoPlayer = ({ player }) => {
    return (
      <Box display="flex" alignItems="center" mb={2}>
        <Avatar src={`${mediaUrl}/photos/${player.id}.png`} alt={player.nom} />
        <Typography style={{ marginLeft: '10px' }}>
          {canViewPlayer ? (
            <Box
              component="a"
              href={`/players/${player.id}`}
              onClick={(e) => {
                e.preventDefault();
                handleViewPlayer(player);
              }}
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  cursor: 'pointer',
                },
              }}
            >
              {player.nom}
            </Box>
          ) : (
            <span>{player.nom}</span>
          )}
        </Typography>
        <IconButton
          onClick={() => removePlayerFromSeguimiento(player)}
          style={{ marginLeft: 'auto' }}
        >
          <DeleteIcon color="error" />
        </IconButton>
      </Box>
    );
  };

  // Zone de dépôt (colonne de droite)
  const SeguimientoDropZone = () => {
    const [{ isOver }, dropRef] = useDrop({
      accept: 'player',
      drop: (player) => movePlayerToSeguimiento(player),
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    });

    return (
      <Paper
        ref={dropRef}
        style={{
          minHeight: '400px',
          backgroundColor: isOver ? '#e0f7fa' : '#f5f5f5',
          padding: '20px',
        }}
      >
        <Typography variant="h6">{t('seguimiento.dropzone')}</Typography>
        {seguimientoPlayers.length === 0 && <p>{t('seguimiento.dragPlayersHere')}</p>}
        {seguimientoPlayers.map((player) => (
          <SeguimientoPlayer key={player.id} player={player} />
        ))}
      </Paper>
    );
  };

  // 13) RENDU FINAL
  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ p: 2 }}>
       

        {/* Filters */}
        <BigBoardFilters
          playerTypeFilter={playerTypeFilter}
          setPlayerTypeFilter={setPlayerTypeFilter}
          informeFilter={informeFilter}
          setInformeFilter={setInformeFilter}
          scoutFilter={scoutFilter}
          setScoutFilter={setScoutFilter}
          verdictFilter={verdictFilter}
          setVerdictFilter={setVerdictFilter}
          nameFilter={nameFilter}
          setNameFilter={setNameFilter}
          yearFilter={yearFilter}
          setYearFilter={setYearFilter}
          positionFilter={positionFilter}
          setPositionFilter={setPositionFilter}
          groupFilter={groupFilter}
          setGroupFilter={setGroupFilter}
          heightFilter={heightFilter}
          setHeightFilter={setHeightFilter}
          ageFilter={ageFilter}
          setAgeFilter={setAgeFilter}
          groups={groups}
          groupColors={groupColors}
          userRole={userRole}
        />

        <Box sx={{ mt: 4, px: 4 }}>

          {/* Configuration du Seguimiento */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {/* Nom du Seguimiento (Select + éventuellement TextField) */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="seguimiento-name-label">{t('seguimiento.seguimientoName')}</InputLabel>
                <Select
                  labelId="seguimiento-name-label"
                  value={selectedSeguimientoNameOption}
                  onChange={handleSeguimientoNameChange}
                  label={t('seguimiento.seguimientoName')}
                >
                  <MenuItem value="">{t('seguimiento.selectOption')}</MenuItem>
                  <MenuItem value="Bases">{t('seguimiento.bases')}</MenuItem>
                  <MenuItem value="Escoltas">{t('seguimiento.escoltas')}</MenuItem>
                  <MenuItem value="Aleros">{t('seguimiento.aleros')}</MenuItem>
                  <MenuItem value="Ala-Pívots">{t('seguimiento.alaPivots')}</MenuItem>
                  <MenuItem value="Pívots">{t('seguimiento.pivots')}</MenuItem>
                  <MenuItem value="Otro">{t('seguimiento.other')}</MenuItem>
                </Select>
              </FormControl>

              {selectedSeguimientoNameOption === 'Otro' && (
                <TextField
                  label={t('seguimiento.otherNamePlaceholder')}
                  variant="outlined"
                  value={seguimientoName}
                  onChange={(e) => setSeguimientoName(e.target.value)}
                  fullWidth
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>

            {/* Tags */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="tags-select-label">{t('seguimiento.selectTags')}</InputLabel>
                <Select
                  labelId="tags-select-label"
                  multiple
                  value={selectedTags}
                  onChange={(e) => setSelectedTags(e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((tagId) => {
                        const tag = tags.find((t) => t.id === tagId);
                        return (
                          <Chip
                            key={tagId}
                            label={tag.libelle}
                            sx={{
                              backgroundColor: tag?.code_couleur || '#555',
                              color: 'white',
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {tags.map((tag) => (
                    <MenuItem key={tag.id} value={tag.id}>
                      <Checkbox checked={selectedTags.indexOf(tag.id) > -1} />
                      <Chip
                        label={tag.libelle}
                        sx={{ backgroundColor: tag.code_couleur, color: 'white', mr: 1 }}
                      />
                      <ListItemText primary={tag.libelle} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Bouton enregistrer */}
          <Button
            variant="contained"
            sx={{ backgroundColor: '#ff9800', color: 'white', mb: 4 }}
            onClick={handleSaveSeguimiento}
          >
            {t('seguimiento.saveSeguimiento')}
          </Button>

          {/* Tableau + Dropzone */}
          <Grid container spacing={2}>
            {/* Tableau (gauche) */}
            <Grid item xs={12} md={8}>
              <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: '#F25F29',
                          color: 'white',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          width: '50px',
                          padding: '8px 4px'
                        }}
                      >
                        <Checkbox
                          disabled
                          sx={{ color: 'white' }}
                        />
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '120px', padding: '8px 4px' }}>
                        <Box sx={{
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          border: '1px solid rgba(255,255,255,0.3)',
                          display: 'inline-block'
                        }}>
                          <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{filteredPlayers.length}</span>
                          <span style={{ fontSize: '9px', opacity: 0.8, marginLeft: '2px' }}>{t('seguimiento.table.players')}</span>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '120px', padding: '8px 4px' }}>
                        {t('seguimiento.table.name')}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '60px', padding: '8px 4px' }}>
                        {t('seguimiento.table.form')}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '80px', padding: '8px 4px' }}>
                        {t('seguimiento.table.position')}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '100px', padding: '8px 4px' }}>
                        {t('seguimiento.table.year')}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '60px', padding: '8px 4px' }}>
                        {t('seguimiento.table.height')}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '100px', padding: '8px 4px' }}>
                        {t('seguimiento.table.nationality')}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '120px', padding: '8px 4px' }}>
                        {t('seguimiento.table.team')}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '80px', padding: '8px 4px' }}>
                        {t('seguimiento.table.groups')}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '80px', padding: '8px 4px' }}>
                        {t('seguimiento.table.informeScout')}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '140px', padding: '8px 4px' }}>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getCurrentPagePlayers().map((player) => (
                      <PlayerRow key={player.id} player={player} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filteredPlayers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                labelRowsPerPage={t('seguimiento.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => t('seguimiento.pagination.displayedRows', { from, to, count })}
              />
            </Grid>

            {/* Dropzone (droite) */}
            <Grid item xs={12} md={4}>
              <SeguimientoDropZone />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </DndProvider>
  );
};

export default Seguimiento;
